import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconGift } from '../../RestaurantHome/RestaurantDetailIcons';
import { couponMatchesOrderType } from '../../../utils/offerFormatting';
import './OffersDrawer.css';

export interface OfferPromotion {
  id: number;
  name: string;
  description?: string;
  discountPreview?: string;
}

export interface OfferCoupon {
  id: number;
  couponCode?: string;
  couponType?: string;
  discount?: number;
  minAmount?: number;
  description?: string;
  orderType?: string;
}

export interface OffersDrawerProps {
  open: boolean;
  onClose: () => void;
  promotions: OfferPromotion[];
  coupons: OfferCoupon[];
  formatCouponBadge: (coupon: OfferCoupon) => string;
  formatCouponText: (coupon: OfferCoupon) => string;
  orderType?: number;
  appliedCouponCode?: string | null;
  appliedPromotionId?: number | null;
  applying?: boolean;
  applyError?: string | null;
  onApplyCoupon?: (code: string) => void;
  onRemoveCoupon?: () => void;
}

const OffersDrawer: React.FC<OffersDrawerProps> = ({
  open,
  onClose,
  promotions,
  coupons,
  formatCouponBadge,
  formatCouponText,
  orderType,
  appliedCouponCode,
  appliedPromotionId,
  applying = false,
  applyError,
  onApplyCoupon,
  onRemoveCoupon,
}) => {
  const [manualCode, setManualCode] = useState('');
  const canApply = typeof onApplyCoupon === 'function';
  const totalCount = promotions.length + coupons.length;

  useEffect(() => {
    if (!open) {
      setManualCode('');
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.classList.add('offers-drawer-open');
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('offers-drawer-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const normalizedApplied = (appliedCouponCode || '').trim().toUpperCase();

  const content = (
    <div className="offers-drawer-overlay" onClick={onClose} role="presentation">
      <div
        className="offers-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offers-drawer-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="offers-drawer__handle" aria-hidden />
        <div className="offers-drawer__header">
          <div className="offers-drawer__title-wrap">
            <span className="offers-drawer__title-icon" aria-hidden>
              <IconGift size={18} />
            </span>
            <div>
              <h2 id="offers-drawer-title">Offers &amp; Deals</h2>
              <p className="offers-drawer__subtitle">
                {totalCount > 0
                  ? `${totalCount} ${totalCount === 1 ? 'deal' : 'deals'} — apply a coupon to this order`
                  : 'Enter a coupon code for this order'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="offers-drawer__close"
            onClick={onClose}
            aria-label="Close offers"
          >
            ×
          </button>
        </div>
        <div className="offers-drawer__body">
          {canApply && (
            <form
              className="offers-drawer__code-form"
              onSubmit={(event) => {
                event.preventDefault();
                const code = manualCode.trim();
                if (code) {
                  onApplyCoupon(code);
                }
              }}
            >
              <input
                className="offers-drawer__code-input"
                value={manualCode}
                onChange={(event) => setManualCode(event.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                autoCapitalize="characters"
                autoComplete="off"
                disabled={applying}
              />
              <button type="submit" className="offers-drawer__apply" disabled={applying || !manualCode.trim()}>
                {applying ? 'Applying…' : 'Apply'}
              </button>
            </form>
          )}
          {applyError && <p className="offers-drawer__error">{applyError}</p>}
          {normalizedApplied && canApply && (
            <div className="offers-drawer__applied">
              <span>
                Applied <strong>{normalizedApplied}</strong>
              </span>
              {onRemoveCoupon && (
                <button type="button" onClick={onRemoveCoupon} disabled={applying}>
                  Remove
                </button>
              )}
            </div>
          )}
          {totalCount > 0 && (
            <div className="offers-drawer__list">
              {promotions.map((promotion) => {
                const autoApplied = appliedPromotionId != null && Number(appliedPromotionId) === Number(promotion.id);
                return (
                  <div key={`p-${promotion.id}`} className="offers-drawer__item">
                    <div className="offers-drawer__badge">{autoApplied ? 'APPLIED' : 'OFFER'}</div>
                    <div className="offers-drawer__text">
                      <strong>{promotion.name}</strong>
                      {promotion.discountPreview
                        ? ` - ${promotion.discountPreview}`
                        : promotion.description
                          ? ` - ${promotion.description}`
                          : ''}
                      <span className="offers-drawer__meta">
                        {autoApplied ? 'Applied automatically on this cart' : 'Applies automatically when your cart qualifies'}
                      </span>
                    </div>
                  </div>
                );
              })}
              {coupons.map((coupon) => {
                const code = (coupon.couponCode || '').trim();
                const isApplied = Boolean(code) && code.toUpperCase() === normalizedApplied;
                const matches = orderType == null || couponMatchesOrderType(coupon.orderType, orderType);
                return (
                  <div key={`c-${coupon.id}`} className="offers-drawer__item">
                    <div className="offers-drawer__badge">{formatCouponBadge(coupon)}</div>
                    <div className="offers-drawer__text">
                      <strong>{code || 'Coupon'}</strong> — {formatCouponText(coupon)}
                      {!matches && (
                        <span className="offers-drawer__meta">Not valid for this order type</span>
                      )}
                    </div>
                    {canApply && code && (
                      <button
                        type="button"
                        className={`offers-drawer__apply${isApplied ? ' offers-drawer__apply--done' : ''}`}
                        disabled={applying || isApplied || !matches}
                        onClick={() => onApplyCoupon(code)}
                      >
                        {isApplied ? 'Applied' : 'Apply'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {!canApply && (
            <p className="offers-drawer__hint">
              Promotions apply automatically at checkout. Enter coupon codes on the cart page when available.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return content;
  return createPortal(content, document.body);
};

export default OffersDrawer;
